/**
 * Custom panel — schema extends the named-panel base with `color` and
 * `icon` attributes. The slash menu inserts a fresh custom panel with
 * `defaultCustomPanelColor` / `defaultCustomPanelIcon`; the bubble-menu
 * picker (see `CustomPanelBubbleMenu.tsx`, mounted by `MarkdownEditor`
 * at the wiki surface) is what lets the user pick a different shade
 * and icon.
 *
 * Why the picker isn't inside the NodeView itself:
 *   A bubble menu portals through tippy.js to document.body. Mounting
 *   it inside the NodeView would tie the popper lifecycle to the
 *   ProseMirror NodeView teardown — same removeChild race the
 *   TableBubbleMenu hit in Phase 3 (see the canonical comment on
 *   `MarkdownEditor.tsx:934`). Keeping the picker as a sibling
 *   `<BubbleMenu>` mounted alongside the editor avoids that whole
 *   class of bug; the NodeView just renders the chrome.
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

import { exitIsolatingBlockDown } from '@/editor/lib/exit-isolating-block-down'
import {
  DEFAULT_CUSTOM_PANEL_COLOR,
  DEFAULT_CUSTOM_PANEL_ICON,
  isCustomPanelColor,
  isCustomPanelIcon,
} from './panel-kinds'

import { buildPanelPlaceholderPlugin } from './panel-placeholder-plugin'

export interface BuildCustomPanelExtensionOptions {
  /**
   * React NodeView component (from `CustomPanelView.tsx`). The
   * factory accepts it as a parameter so the test suite can stub it
   * out with a no-op view that still renders `NodeViewContent`.
   * `ReactNodeViewProps<HTMLElement>` matches what
   * `ReactNodeViewRenderer<T = HTMLElement>(component)` expects.
   */
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildCustomPanelExtension({ view }: BuildCustomPanelExtensionOptions) {
  return Node.create({
    name: 'customPanel',
    group: 'block',
    content: 'block+',
    defining: true,
    isolating: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        color: {
          default: DEFAULT_CUSTOM_PANEL_COLOR,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-color')
            // Fall back to the default if the saved color isn't one
            // we recognize — keeps a doc readable across schema bumps.
            return isCustomPanelColor(raw) ? raw : DEFAULT_CUSTOM_PANEL_COLOR
          },
          renderHTML: (attrs: { color: string }) => ({ 'data-color': attrs.color }),
        },
        icon: {
          default: DEFAULT_CUSTOM_PANEL_ICON,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-icon')
            return isCustomPanelIcon(raw) ? raw : DEFAULT_CUSTOM_PANEL_ICON
          },
          renderHTML: (attrs: { icon: string }) => ({ 'data-icon': attrs.icon }),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'div[data-block-type="custom-panel"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-block-type': 'custom-panel' }, 0]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },

    addKeyboardShortcuts() {
      // Mirrors the named-panel factory: ArrowDown at end of body
      // exits into a paragraph below to avoid the gap cursor.
      const exit = exitIsolatingBlockDown('customPanel')
      return {
        ArrowDown: ({ editor }) => editor.commands.command(exit),
        'Mod-Enter': ({ editor }) => editor.commands.command(exit),
      }
    },

    addProseMirrorPlugins() {
      return [
        buildPanelPlaceholderPlugin({
          panelNodeName: 'customPanel',
          translationKey: 'wiki:panel.placeholder',
        }),
      ]
    },
  })
}
